import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/hooks/helpers'
import type { OrganisationAnalyticsShowResponse } from '@jobilla/nexus-sdk'
import { Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { type CampaignRow, periodLabel, targetAreaLabel } from './breakdown-format'

export type BreakdownTableProps = {
	rows: CampaignRow[]
	meta: OrganisationAnalyticsShowResponse['breakdown']['meta']
	onPageChange: (page: number) => void
}

type NumColor = 'grey' | 'emerald' | 'amber' | 'rose'

const numPalettes: Record<NumColor, { zero: string; value: string }> = {
	grey: { zero: 'text-grey-300', value: 'text-grey-700' },
	emerald: { zero: 'text-emerald-300', value: 'text-emerald-700' },
	amber: { zero: 'text-amber-300', value: 'text-amber-600' },
	rose: { zero: 'text-rose-300', value: 'text-rose-500' },
}

function Num({ value, color = 'grey' }: { value: number; color?: NumColor }) {
	const palette = numPalettes[color]
	return (
		<span className={value === 0 ? palette.zero : palette.value}>
			{value.toLocaleString()}
		</span>
	)
}

export function BreakdownTable({ rows, meta, onPageChange }: BreakdownTableProps) {
	const { t } = useTranslation('analytics')
	const tp = (key: string) => t(`organisationAnalytics.breakdown.${key}`)
	const { companyId } = useParams({ strict: false })

	return (
		<div className="space-y-4">
			<div className="overflow-x-auto rounded-lg border border-grey-200 bg-white">
				<table className="w-full text-sm border-collapse min-w-[1200px]">
					<thead>
						<tr className="bg-grey-50 text-grey-400 text-[10px] uppercase tracking-wider">
							<th colSpan={5} />
							<th colSpan={3} className="py-1.5 px-2 text-center font-medium border-l border-grey-200">
								{tp('funnel')}
							</th>
							<th colSpan={3} className="py-1.5 px-2 text-center font-medium border-l border-grey-200">
								{tp('scoreBands')}
							</th>
							<th colSpan={4} className="py-1.5 px-2 text-center font-medium border-l border-grey-200">
								{tp('pipeline')}
							</th>
							<th colSpan={2} className="border-l border-grey-200" />
						</tr>
						<tr className="bg-grey-50 border-b border-grey-200 text-grey-500 text-xs [&>th]:align-top">
							<th className={`text-left py-2 px-3 font-medium bg-grey-50 ${STICKY_COLUMN}`}>
								{tp('campaign')}
							</th>
							<th className="text-left py-2 px-3 font-medium">{tp('status')}</th>
							<th className="text-left py-2 px-3 font-medium">{tp('location')}</th>
							<th className="text-left py-2 px-3 font-medium">{tp('targetArea')}</th>
							<th className="text-left py-2 px-3 font-medium">{tp('period')}</th>
							<th className="text-right py-2 px-2 font-medium border-l border-grey-200">{tp('views')}</th>
							<th className="text-right py-2 px-2 font-medium">{tp('clicks')}</th>
							<th className="text-right py-2 px-2 font-medium">{tp('candidates')}</th>
							<th className="text-right py-2 px-2 font-medium border-l border-grey-200">
								<div className="flex flex-col items-end leading-tight">
									<span>{tp('excellent')}</span>
									<span className="text-[11px] font-normal text-grey-500 tabular-nums mt-0.5">
										89-100
									</span>
								</div>
							</th>
							<th className="text-right py-2 px-2 font-medium">
								<div className="flex flex-col items-end leading-tight">
									<span>{tp('good')}</span>
									<span className="text-[11px] font-normal text-grey-500 tabular-nums mt-0.5">
										66-88
									</span>
								</div>
							</th>
							<th className="text-right py-2 px-2 font-medium">
								<div className="flex flex-col items-end leading-tight">
									<span>{tp('noMatch')}</span>
									<span className="text-[11px] font-normal text-grey-500 tabular-nums mt-0.5">
										0-65
									</span>
								</div>
							</th>
							<th className="text-right py-2 px-2 font-medium border-l border-grey-200">{tp('new')}</th>
							<th className="text-right py-2 px-2 font-medium">{tp('other')}</th>
							<th className="text-right py-2 px-2 font-medium">{tp('rejected')}</th>
							<th className="text-right py-2 px-2 font-medium">{tp('hired')}</th>
							<th className="text-right py-2 px-2 font-medium border-l border-grey-200">
								{tp('budget')}
							</th>
							<th className="text-right py-2 px-2 font-medium">{tp('costPerCandidate')}</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, i) => (
							<tr
								key={row.id}
								className={`border-b border-grey-100 hover:bg-[color-mix(in_srgb,var(--color-blue-50)_40%,white)] transition-colors ${
									i % 2 === 1 ? 'bg-[color-mix(in_srgb,var(--color-grey-50)_30%,white)]' : 'bg-white'
								}`}
							>
								<td
									className={`py-2.5 px-3 font-medium text-grey-900 min-w-[150px] max-w-[260px] bg-inherit ${STICKY_COLUMN}`}
								>
									{companyId
										? (
											<Link
												to="/company/$companyId/$campaignId/overview"
												params={{ companyId, campaignId: row.id }}
												className="line-clamp-2 text-blue-600 hover:text-blue-700 hover:underline"
											>
												{row.title}
											</Link>
										)
										: <span className="line-clamp-2">{row.title}</span>}
								</td>
								<td className="py-2.5 px-3 whitespace-nowrap">
									<StatusBadge status={row.status} />
								</td>
								<td className="py-2.5 px-3 text-grey-700">
									{row.location ?? <span className="text-grey-300">—</span>}
								</td>
								<td className="py-2.5 px-3 text-grey-700 max-w-[200px]">
									{targetAreaLabel(row) ?? <span className="text-grey-300">—</span>}
								</td>
								<td className="py-2.5 px-3 text-grey-700 whitespace-nowrap">
									{row.isEvergreen
										? <span className="text-grey-500">{tp('ongoing')}</span>
										: <PeriodCell label={periodLabel(row)} />}
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums border-l border-grey-100">
									<Num value={row.funnel.views} />
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums">
									<Num value={row.funnel.clicks} />
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums">
									<Num value={row.funnel.candidates} />
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums border-l border-grey-100">
									<Num value={row.scoreBands.excellent} color="emerald" />
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums">
									<Num value={row.scoreBands.good} color="amber" />
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums">
									<Num value={row.scoreBands.bad} color="rose" />
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums border-l border-grey-100">
									<Num value={row.pipeline.new} />
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums">
									<Num value={row.pipeline.other} />
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums">
									<Num value={row.pipeline.rejected} />
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums">
									<Num value={row.pipeline.hired} />
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums whitespace-nowrap border-l border-grey-100">
									{row.budget > 0
										? <span className="text-grey-700">{formatCurrency(row.budget / 100)}</span>
										: <span className="text-grey-300">—</span>}
								</td>
								<td className="py-2.5 px-2 text-right tabular-nums whitespace-nowrap">
									{row.costPerCandidate !== null
										? (
											<span className="text-grey-700">
												{formatCurrency(row.costPerCandidate / 100)}
											</span>
										)
										: <span className="text-grey-300">—</span>}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{meta.lastPage > 1 && (
				<div className="flex items-center justify-between">
					<Button
						variant="outline"
						size="sm"
						onClick={() => onPageChange(meta.currentPage - 1)}
						disabled={meta.currentPage <= 1}
					>
						{tp('pagination.previous')}
					</Button>
					<span className="text-sm text-grey-500">
						{t('organisationAnalytics.breakdown.pagination.page', {
							current: meta.currentPage,
							total: meta.lastPage,
						})}
					</span>
					<Button
						variant="outline"
						size="sm"
						onClick={() => onPageChange(meta.currentPage + 1)}
						disabled={meta.currentPage >= meta.lastPage}
					>
						{tp('pagination.next')}
					</Button>
				</div>
			)}
		</div>
	)
}

const STICKY_COLUMN = 'sticky left-0 z-10 shadow-[inset_-1px_0_0_var(--color-grey-200)]'

function PeriodCell({ label }: { label: string | null }) {
	if (!label) { return <span className="text-grey-300">—</span> }
	const [start, end] = label.split(' – ')
	return (
		<>
			<span className="block">{start} –</span>
			<span className="block">{end}</span>
		</>
	)
}

function StatusBadge({ status }: { status: CampaignRow['status'] }) {
	const { t } = useTranslation('analytics')
	const colors: Record<CampaignRow['status'], string> = {
		draft: 'bg-grey-100 text-grey-600',
		review: 'bg-amber-100 text-amber-700',
		publishing: 'bg-blue-100 text-blue-700',
		active: 'bg-emerald-100 text-emerald-700',
		completed: 'bg-slate-100 text-slate-600',
	}

	const label = t(`breakdown.status.${status}`)

	return (
		<span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}>
			{label}
		</span>
	)
}

const skeletonWidths = [75, 60, 85, 68, 90]

export function BreakdownTableSkeleton() {
	return (
		<div className="rounded-lg border border-grey-200 overflow-hidden bg-white">
			<div className="h-10 bg-grey-50" />
			{skeletonWidths.map((width, i) => (
				<div
					key={i}
					className={`h-11 border-t border-grey-100 ${i % 2 === 1 ? 'bg-grey-50/30' : ''}`}
				>
					<div
						className="h-4 mx-3 my-3.5 bg-grey-100 rounded animate-pulse"
						style={{ width: `${width}%` }}
					/>
				</div>
			))}
		</div>
	)
}
